import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AdminUser, adminApi, AdminLoginResponsePayload } from '../api/admin';
import { getErrorMessage } from '../api/client';

export type AdminLoginStep = 'ok' | 'needs-2fa' | 'needs-mfa-setup';

interface AdminAuthState {
  adminUser: AdminUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  pendingTwoFactorToken: string | null;

  login: (email: string, password: string) => Promise<AdminLoginStep>;
  loginWithTwoFactor: (token: string, code: string) => Promise<void>;
  completeMandatoryMfaAndSignIn: (setupToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  syncProfile: () => Promise<void>;
  updateProfile: (data: Partial<AdminUser>) => Promise<void>;
  clearError: () => void;
  setSessionFromPayload: (payload: AdminLoginResponsePayload) => void;
}

function persistUserFromPayload(prev: AdminUser | null, payload: AdminLoginResponsePayload): AdminUser | null {
  if (payload.user) return payload.user;
  return prev;
}

export const useAdminAuthStore = create<AdminAuthState>()(
  persist(
    (set, get) => ({
      adminUser: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      pendingTwoFactorToken: null,

      setSessionFromPayload: (payload: AdminLoginResponsePayload) => {
        const accessToken = payload.accessToken;
        const refreshToken = payload.refreshToken;
        if (!accessToken || !refreshToken) return;
        const adminUser = persistUserFromPayload(get().adminUser, payload);
        if (!adminUser) return;
        set({
          adminUser,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          pendingTwoFactorToken: null,
        });
        localStorage.setItem('adminToken', accessToken);
        localStorage.setItem('adminRefreshToken', refreshToken);
      },

      login: async (email: string, password: string): Promise<AdminLoginStep> => {
        set({ isLoading: true, error: null, pendingTwoFactorToken: null });

        try {
          const { data } = await adminApi.login({ email, password, rememberMe: true });

          if (data.requiresTwoFactorSetup && data.twoFactorSetupToken) {
            sessionStorage.setItem('admin_mfa_setup_token', data.twoFactorSetupToken);
            set({ isLoading: false, isAuthenticated: false, error: null });
            return 'needs-mfa-setup';
          }

          if (data.requiresTwoFactor && data.twoFactorToken) {
            set({
              isLoading: false,
              isAuthenticated: false,
              pendingTwoFactorToken: data.twoFactorToken,
              error: null,
            });
            return 'needs-2fa';
          }

          if (!data.accessToken || !data.refreshToken) {
            set({ isLoading: false, error: 'Unexpected login response from server.' });
            throw new Error('Unexpected login response');
          }

          const adminUser = data.user;
          if (!adminUser) {
            set({ isLoading: false, error: 'User profile missing from login response.' });
            throw new Error('User profile missing');
          }

          set({
            adminUser,
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            pendingTwoFactorToken: null,
          });

          localStorage.setItem('adminToken', data.accessToken);
          localStorage.setItem('adminRefreshToken', data.refreshToken);
          return 'ok';
        } catch (error: unknown) {
          const errorMessage = getErrorMessage(error, 'Login failed');
          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      loginWithTwoFactor: async (token: string, code: string) => {
        set({ isLoading: true, error: null });

        try {
          const { data } = await adminApi.verifyTwoFactor(token, code);
          get().setSessionFromPayload(data);
          if (!get().isAuthenticated) {
            set({ isLoading: false, error: 'Two-factor verification did not return tokens.' });
            throw new Error('Invalid 2FA completion');
          }
        } catch (error: unknown) {
          const errorMessage = getErrorMessage(error, 'Two-factor verification failed');
          set({ isLoading: false, error: errorMessage });
          throw error;
        }
      },

      completeMandatoryMfaAndSignIn: async (setupToken: string, code: string) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await adminApi.confirmMandatoryTwoFactorSetup(setupToken, code);
          get().setSessionFromPayload(data);
          sessionStorage.removeItem('admin_mfa_setup_token');
          if (!get().isAuthenticated) {
            set({ isLoading: false, error: 'Could not complete sign-in.' });
            throw new Error('Invalid MFA completion');
          }
        } catch (error: unknown) {
          const errorMessage = getErrorMessage(error, 'Could not enable two-factor authentication');
          set({ isLoading: false, error: errorMessage });
          throw error;
        }
      },

      logout: async () => {
        try {
          await adminApi.logout();
        } catch {
          /* ignore */
        } finally {
          set({
            adminUser: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            error: null,
            pendingTwoFactorToken: null,
          });
          sessionStorage.removeItem('admin_mfa_setup_token');
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminRefreshToken');
        }
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        try {
          const { data } = await adminApi.refreshToken(refreshToken);
          if (!data.accessToken || !data.refreshToken) throw new Error('Invalid refresh response');
          const adminUser = persistUserFromPayload(get().adminUser, data);
          if (!adminUser) throw new Error('User missing after refresh');

          set({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            adminUser,
          });

          localStorage.setItem('adminToken', data.accessToken);
          localStorage.setItem('adminRefreshToken', data.refreshToken);
        } catch (error) {
          get().logout();
          throw error;
        }
      },

      syncProfile: async () => {
        const { accessToken } = get();
        if (!accessToken) return;
        try {
          const { data } = await adminApi.getCurrentUser();
          set({ adminUser: data });
        } catch {
          /* optional: offline */
        }
      },

      updateProfile: async (data: Partial<AdminUser>) => {
        set({ isLoading: true, error: null });

        try {
          const response = await adminApi.updateProfile({
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber,
          });
          const updatedUser = response.data;

          set({
            adminUser: updatedUser,
            isLoading: false,
            error: null,
          });
        } catch (error: unknown) {
          const errorMessage = getErrorMessage(error, 'Profile update failed');
          set({
            isLoading: false,
            error: errorMessage,
          });
          throw error;
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'admin-auth-storage',
      partialize: (state) => ({
        adminUser: state.adminUser,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
